import type {
  PolygonStyle,
  SurveyPolygon,
} from "@/features/polygon/types";
import { createApi } from "@reduxjs/toolkit/query/react";
import { interactiveSurveyBaseQuery } from "./base";

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  value !== null && typeof value === "object";

const asFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const asBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) return true;
    if (["false", "0", "no", "n", ""].includes(normalized)) return false;
  }
  return fallback;
};

const tryParseJson = (value: unknown): unknown => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const normalizeLngLat = (first: number, second: number): [number, number] => {
  // If first looks like latitude and second looks like longitude, swap.
  if (Math.abs(first) <= 90 && Math.abs(second) > 90) {
    return [second, first];
  }
  return [first, second];
};

const toCoordinatePair = (value: unknown): [number, number] | null => {
  if (Array.isArray(value) && value.length >= 2) {
    const lng = asFiniteNumber(value[0]);
    const lat = asFiniteNumber(value[1]);
    if (lng != null && lat != null) return normalizeLngLat(lng, lat);
  }

  if (isRecord(value)) {
    const lng = asFiniteNumber(
      value.longitude ?? value.lng ?? value.lon ?? value.x ?? value.long ?? value.Longitude,
    );
    const lat = asFiniteNumber(
      value.latitude ?? value.lat ?? value.y ?? value.Latitude,
    );
    if (lng != null && lat != null) return [lng, lat];
  }

  return null;
};

const ensureClosedRing = (ring: [number, number][]): [number, number][] => {
  if (ring.length < 3) return ring;
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return ring;
  return [...ring, [first[0], first[1]]];
};

const normalizeCoordinates = (value: unknown): number[][][] => {
  const parsedValue = tryParseJson(value);
  if (!Array.isArray(parsedValue) || parsedValue.length === 0) return [];

  // MultiPolygon coordinates: [ [ [lng,lat], ... ], [ [lng,lat], ... ] ] ...
  // We normalize by taking the first polygon's rings when this shape is detected.
  if (
    Array.isArray(parsedValue[0]) &&
    parsedValue[0].length > 0 &&
    Array.isArray(parsedValue[0][0]) &&
    parsedValue[0][0].length > 0 &&
    Array.isArray(parsedValue[0][0][0])
  ) {
    const firstPolygon = parsedValue[0] as unknown[];
    return firstPolygon
      .map((ring) =>
        Array.isArray(ring)
          ? ensureClosedRing(
              ring
                .map((point) => toCoordinatePair(point))
                .filter((point): point is [number, number] => point != null),
            )
          : [],
      )
      .filter((ring) => ring.length >= 3);
  }

  const first = parsedValue[0];
  if (Array.isArray(first) && first.length > 0 && Array.isArray(first[0])) {
    const rings = parsedValue as unknown[];
    return rings
      .map((ring) =>
        Array.isArray(ring)
          ? ensureClosedRing(
              ring
                .map((point) => toCoordinatePair(point))
                .filter((point): point is [number, number] => point != null),
            )
          : [],
      )
      .filter((ring) => ring.length >= 3);
  }

  const oneRing = ensureClosedRing(
    parsedValue
      .map((point) => toCoordinatePair(point))
      .filter((point): point is [number, number] => point != null),
  );

  return oneRing.length >= 3 ? [oneRing] : [];
};

const normalizeStyle = (polygon: UnknownRecord): PolygonStyle => {
  const styleNode = isRecord(polygon.style)
    ? polygon.style
    : isRecord(polygon.mapData)
      ? polygon.mapData
      : ({} as UnknownRecord);

  const borderDasharray = Array.isArray(styleNode.borderDasharray)
    ? styleNode.borderDasharray
        .map((value) => asFiniteNumber(value))
        .filter((value): value is number => value != null)
    : undefined;

  return {
    fillColor:
      (styleNode.fillColor as string | undefined) ??
      (styleNode.color as string | undefined) ??
      (polygon.fillColor as string | undefined) ??
      "#1f7a8c",
    fillOpacity:
      asFiniteNumber(styleNode.fillOpacity) ??
      asFiniteNumber(styleNode.opacity) ??
      asFiniteNumber(polygon.fillOpacity) ??
      0.28,
    borderColor:
      (styleNode.borderColor as string | undefined) ??
      (styleNode.strokeColor as string | undefined) ??
      (styleNode.lineColor as string | undefined) ??
      (polygon.borderColor as string | undefined) ??
      "#1f7a8c",
    borderOpacity:
      asFiniteNumber(styleNode.borderOpacity) ??
      asFiniteNumber(styleNode.strokeOpacity) ??
      asFiniteNumber(polygon.borderOpacity) ??
      1,
    borderWidth:
      asFiniteNumber(styleNode.borderWidth) ??
      asFiniteNumber(styleNode.strokeWidth) ??
      asFiniteNumber(styleNode.size) ??
      asFiniteNumber(polygon.borderWidth) ??
      2,
    borderDasharray,
  };
};

const normalizePolygon = (item: unknown): SurveyPolygon | null => {
  if (!isRecord(item)) return null;

  const parsedItem = isRecord(tryParseJson(item))
    ? (tryParseJson(item) as UnknownRecord)
    : item;

  const propertiesNode = isRecord(parsedItem.properties)
    ? parsedItem.properties
    : ({} as UnknownRecord);
  const geometryNode = isRecord(parsedItem.geometry)
    ? parsedItem.geometry
    : ({} as UnknownRecord);

  const coordinates = normalizeCoordinates(
    geometryNode.coordinates ?? parsedItem.coordinates,
  );

  if (coordinates.length === 0) return null;

  return {
    polygonId:
      String(
        parsedItem.polygonId ??
          parsedItem.id ??
          parsedItem.extId ??
          propertiesNode.polygonId ??
          propertiesNode.id ??
          parsedItem.name ??
          "",
      ) ||
      "polygon",
    surveyId:
      (parsedItem.surveyId as string | undefined) ??
      (propertiesNode.surveyId as string | undefined) ??
      undefined,
    name: String(
      parsedItem.name ??
        parsedItem.title ??
        parsedItem.label ??
        propertiesNode.name ??
        "Polygon",
    ),
    isHidden: asBoolean(parsedItem.isHidden ?? propertiesNode.isHidden, false),
    showLabel: asBoolean(parsedItem.showLabel ?? propertiesNode.showLabel, true),
    order: asFiniteNumber(parsedItem.order ?? propertiesNode.order),
    coordinates,
    style: normalizeStyle({ ...propertiesNode, ...parsedItem }),
  };
};

const extractPolygonList = (response: unknown): unknown[] => {
  const parsed = tryParseJson(response);
  
  if (Array.isArray(parsed)) return parsed;
  if (!isRecord(parsed)) return [];

  const result = parsed as { 
    polygons?: unknown[];
    data?: unknown[];
    features?: unknown[];
    result?: UnknownRecord;
  };
  
  const list = result.polygons ?? result.data ?? result.features;
  if (Array.isArray(list)) return list;

  if (isRecord(result.result)) {
    const nested = result.result.polygons ?? result.result.data ?? result.result.features;
    if (Array.isArray(nested)) return nested;
  }

  return isRecord(parsed) ? [parsed] : [];
};

const normalizePolygonList = (response: unknown): SurveyPolygon[] => {
  const list = extractPolygonList(response);
  
  return list
    .map((item) => normalizePolygon(item))
    .filter((item): item is SurveyPolygon => item != null)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

export const polygonApi = createApi({
  reducerPath: "polygonApi",
  baseQuery: interactiveSurveyBaseQuery,
  tagTypes: ["Polygon"],
  endpoints: (build) => ({
    getPolygons: build.query<SurveyPolygon[], string>({
      query: (surveyId) => ({
        url: `polygons/${surveyId}`,
        method: "GET",
      }),
      transformResponse: normalizePolygonList,
      providesTags: [{ type: "Polygon", id: "LIST" }],
    }),
  }),
});

export const { useGetPolygonsQuery } = polygonApi;
